import { useEffect } from 'react';
import { Link } from 'react-router';
import { APP_NAME } from '@sellit/shared';
import { PoweredBy, Wordmark } from '../lib/Brand';
import { setAppTitle } from '../lib/format';
import { Icon, type IconName } from '../lib/Icon';
import { PolicySection as Section } from '../lib/Policy';

const CONTACT = 'sellit@10digits.net';
/** Change only when the policy's substance changes, not for wording or styling. */
const LAST_UPDATED = 'October 25, 2026';

const GLANCE: { icon: IconName; title: string; text: string }[] = [
  { icon: 'shield', title: "We don't sell your data", text: `${APP_NAME} does not sell personal information.` },
  {
    icon: 'lock',
    title: 'Buyer details stay private',
    text: 'Buyer contact details go to the sellers of that sale so they can reply. 10 Digits staff and the services we use to run VenLink can access them only to operate it.',
  },
  { icon: 'pin', title: "Your street address isn't public", text: 'Sale pages show only the general area the seller chooses.' },
];

// Plain-language privacy policy. Every statement here must match what the code and server actually do;
// see the privacy review in the pull request that last changed this file.
export function PrivacyPage() {
  useEffect(() => setAppTitle('Privacy Policy'), []);
  const mail = <a href={`mailto:${CONTACT}`}>{CONTACT}</a>;
  return (
    <>
      <main className="page policy">
        <nav className="policy-top">
          <Link to="/" className="policy-brand" aria-label={`${APP_NAME} home`}><Wordmark className="wordmark" /></Link>
          <Link to="/" className="link"><Icon name="back" size={18} /> Back to {APP_NAME}</Link>
        </nav>

        <header className="policy-head">
          <h1>Privacy Policy</h1>
          <p className="policy-tagline">Your information. Your privacy. Our commitment.</p>
          <p className="muted small">Operated by 10 Digits LLC · Last updated {LAST_UPDATED}</p>
        </header>

        <section className="policy-glance" aria-labelledby="glance">
          <h2 id="glance">Privacy at a glance</h2>
          <ul>
            {GLANCE.map((g) => (
              <li key={g.title}>
                <span className="feature-icon"><Icon name={g.icon} size={20} /></span>
                <span>
                  <strong>{g.title}</strong>
                  <span className="muted">{g.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <Section n={1} title="Information we collect from buyers">
          <p>When you tap "I want this" or make an offer on an item, you give us:</p>
          <ul>
            <li>Your name and phone number.</li>
            <li>Your email address, if you choose to add it.</li>
            <li>Your message and the amount of any offer.</li>
          </ul>
          <p>
            To limit spam, we also store a salted, one-way hash of your IP address with the inquiry (see "Spam prevention"
            below). Buyers don't need an account.
          </p>
        </Section>

        <Section n={2} title="Information we collect from sellers">
          <p>
            <strong>If you ask to start selling,</strong> the Start Selling form sends us your name, email address and, if
            you add one, a sale name, plus a hashed IP address for spam limits. We use it only to set up your account and
            contact you about it.
          </p>
          <p><strong>Private account information:</strong> your name, your email address and your password, which we store only as a secure hash (argon2). We also record when you last signed in, and when you accepted which version of the Terms of Service and this Privacy Policy.</p>
          <p><strong>Public listing information,</strong> which you choose to publish:</p>
          <ul>
            <li>Sale name, description and general location, such as a neighborhood or city.</li>
            <li>Item photos, names, descriptions, condition and prices.</li>
            <li>Your "how to buy" note, if you write one.</li>
          </ul>
          <p>
            <strong>Private sale information:</strong> a street address, if you add one. It is never shown on public pages or
            returned by the public parts of {APP_NAME}.
          </p>
        </Section>

        <Section n={3} title="How we use your information">
          <ul>
            <li>To show and manage sale pages.</li>
            <li>To pass a buyer's inquiry to the sellers of that sale, including by email.</li>
            <li>To sign sellers in and keep their accounts secure.</li>
            <li>To send account emails, such as a link to set your password.</li>
            <li>To prevent spam, fraud and abuse.</li>
            <li>To give technical support and fix problems.</li>
          </ul>
          <p>We don't use your information for advertising, and we don't sell it.</p>
        </Section>

        <Section n={4} title="Who can see your information">
          <ul>
            <li><strong>Anyone with the link</strong> can see a sale's public listing information. Buyer details are never shown publicly.</li>
            <li><strong>The sellers of a sale</strong> (its owners and editors) see the inquiries for that sale's items, including each buyer's name, phone, email and message. They don't see inquiries for other sellers' sales.</li>
            <li><strong>{APP_NAME} administrators</strong> at 10 Digits LLC can access sales, accounts and inquiries to run the service, give support and deal with abuse. Their administrative actions are logged.</li>
            <li><strong>Service providers</strong> process information on our behalf (see section 11).</li>
          </ul>
          <p>We may also disclose information if the law requires it, or to protect people's safety.</p>
        </Section>

        <Section n={5} title="Photos and location privacy">
          <p>
            Every uploaded photo is re-encoded before it is stored. The copies we keep and publish don't contain EXIF, XMP or
            ICC metadata, which includes GPS location and camera details. The original upload is deleted as soon as it has
            been processed.
          </p>
          <p>
            A photo can still show where it was taken, such as a house number or a view from a window, so check your photos
            before posting.
          </p>
          <p>
            Sale pages show a general location that the seller types in, such as "Heights, Houston". A street address, if
            the seller adds one, stays private.
          </p>
        </Section>

        <Section n={6} title="Cookies and storage on your device">
          <ul>
            <li><strong>Buyers:</strong> {APP_NAME} sets no cookies for buyers and visitors. To save typing, your browser remembers the name, phone and email you entered in the "I want this" form, on your device only. Clearing your browser's site data removes it.</li>
            <li><strong>Sellers:</strong> one sign-in cookie keeps you signed in. It is HttpOnly and Secure, and it expires after 30 days without use, or after 90 days at most. Your browser also keeps an unfinished item and your last price choice on your device.</li>
            <li><strong>No analytics or ads:</strong> {APP_NAME} has no analytics, advertising or third-party tracking scripts. Pages load only from {APP_NAME}'s own server.</li>
          </ul>
        </Section>

        <Section n={7} title="Security">
          <p>We protect your information with reasonable measures, including:</p>
          <ul>
            <li>Encrypted connections (HTTPS) on every page.</li>
            <li>Passwords stored only as secure hashes, account lockout after repeated wrong passwords, and setup links that work only once.</li>
            <li>Sellers can reach only the sales they belong to. Administrator access is checked by the server on every request.</li>
            <li>Rate limits on sign-in, uploads and inquiries.</li>
          </ul>
          <p>No system is perfectly secure, and we can't guarantee that information will never be accessed without permission.</p>
        </Section>

        <Section n={8} title="Data retention and deletion">
          <ul>
            <li><strong>Accounts</strong> are kept until you ask us to delete them. Disabling an account signs it out everywhere and stops sign-in.</li>
            <li><strong>Buyer inquiries</strong> are kept with the sale until the item or the sale is deleted. Deleting an item deletes its photos and inquiries.</li>
            <li><strong>Ended sales</strong> are usually archived (hidden from the public) rather than deleted. We delete a sale and everything in it on request.</li>
            <li><strong>Photos</strong> uploaded but never posted are deleted automatically after about a day.</li>
            <li><strong>Backups:</strong> nightly backups are kept for about 14 days, a copy taken before each software update is kept for the last 10 updates, and our hosting provider keeps backups for about two weeks. Deleted information can remain in these backups until they are replaced.</li>
            <li><strong>Server logs</strong> record IP addresses and pages requested, for security and troubleshooting, and are removed on a rolling basis.</li>
          </ul>
          <p>To have something deleted, email {mail}. We'll confirm when it's done.</p>
        </Section>

        <Section n={9} title="Search engines and public sale pages">
          <p>
            Sale pages are meant to be shared by link. Anyone who has the link can open a sale page and its items. Our pages
            ask search engines not to index them, but this is a request: we can't guarantee that a search engine or another
            site will never list a page or a photo.
          </p>
        </Section>

        <Section n={10} title="Your privacy rights and choices">
          <p>You can ask us to:</p>
          <ul>
            <li>Tell you what information we hold about you, and give you a copy.</li>
            <li>Correct information that's wrong. Sellers can edit their own listings at any time.</li>
            <li>Delete your information.</li>
          </ul>
          <p>
            Email {mail}. We may need to confirm who you are first. We may keep some information when the law requires it or
            when it's needed to prevent fraud or abuse, and we'll tell you if so. Buyers can also contact the seller directly.
          </p>
        </Section>

        <Section n={11} title="Service providers">
          <ul>
            <li><strong>Hosting and backups:</strong> Linode (Akamai) runs our server, database, photo storage and backups.</li>
            <li><strong>Email:</strong> Microsoft 365 sends our emails, such as inquiry notices to sellers and set-password links. Inquiry emails include the buyer's details, and they are also handled by the seller's own email provider.</li>
            <li><strong>Security certificates:</strong> Let's Encrypt issues the certificate for our encrypted connection. It receives no personal information.</li>
          </ul>
          <p>{APP_NAME} doesn't currently use AI services, analytics or advertising networks. We will update this policy before that changes.</p>
        </Section>

        <Section n={12} title="Spam prevention">
          <p>
            We store a hash of each inquiry's IP address, made with a secret key. It lets us spot repeated inquiries from the
            same connection without storing the address in the inquiry itself. It is not anonymous: hashes from the same address
            match each other, and someone holding our secret key could test whether a hash came from a particular address.
          </p>
        </Section>

        <Section n={13} title="Changes to this policy">
          <p>
            When we make an important change, we'll update the date at the top. Where we can, we'll also let sellers know
            by email or in the app before the change takes effect. Small wording fixes don't change the date.
          </p>
        </Section>

        <Section n={14} title="Contact us">
          <p>
            {APP_NAME} is operated by 10 Digits LLC. For privacy questions or requests, email {mail}.
          </p>
        </Section>
      </main>
      <PoweredBy />
    </>
  );
}
